import type { AsyncDataOptions } from '#app'
import { useAsyncData, useNuxtData } from '#imports'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import {
  SALES_CUSTOMER_CARE_SCOPE,
  type SalesCustomerCareScope,
} from '@/scopes/care-commons/utils/logger/salesCustomerCare/config'
import { useSalesCustomerCareLogger } from '@/scopes/care-commons/utils/logger/salesCustomerCare/useSalesCustomerCareLogger'
import type { OrderlineLightDetails } from '~/api/my-orders/types/OrderlineLightDetails.types'

export function useOrderlineLightDetails(
  orderlineId: string,
  scope: SalesCustomerCareScope = SALES_CUSTOMER_CARE_SCOPE.diagnosis,
  options: Partial<AsyncDataOptions<OrderlineLightDetails>> = {
    immediate: false,
  },
) {
  const { logHttpError } = useSalesCustomerCareLogger()
  const $httpFetch = useHttpFetch()

  const getOrderlineLightDetailsKey = getAsyncDataKey(
    'get-orderline-light-details',
    {
      orderlineId,
    },
  )

  return useAsyncData(
    getOrderlineLightDetailsKey,
    () =>
      $httpFetch('/my-orders/orderlines/:orderlineId/light', {
        pathParams: {
          orderlineId,
        },
        onResponseError: ({ response }) => {
          if (response) {
            logHttpError({
              // eslint-disable-next-line no-underscore-dangle
              error: response._data as Error,
              errorStatusCode: response.status,
              errorTitle: `${scope} [Orderline #${orderlineId}] Error when fetching orderline light details`,
            })
          }
        },
      }),
    {
      getCachedData: (key) => useNuxtData(key).data.value || undefined,
      ...options,
    },
  )
}
